import React from "react";
import { PageHero, Reveal, Label, CTASection } from "../components/Shared";
import Ribbon from "../components/Ribbon";
import { CAPABILITIES, IMAGES } from "../data/site";

export default function Capabilities() {
  return (
    <>
      <PageHero
        label="Our Capabilities"
        titleLines={["From concept to", "finished, labeled", "product."]}
        intro="A full-service private label manufacturing capability — cleanroom production, custom formulation and complete documentation, all under one certified roof."
        image={IMAGES.equipment}
        imageAlt="Precision manufacturing equipment"
      />
      <Ribbon />

      <section className="bg-[#FAFAFA] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="grid grid-cols-1 border-t border-l border-slate-200 md:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((c, i) => (
              <Reveal
                key={c.no}
                delay={(i % 3) * 0.08}
                className="group border-b border-r border-slate-200 bg-white"
              >
                <div className="flex h-full flex-col p-8 transition-colors duration-300 hover:bg-navy lg:p-10">
                  <span className="font-display text-sm font-bold text-clinical">
                    {c.no}
                  </span>
                  <h3 className="mt-8 font-heading text-2xl font-bold text-navy transition-colors duration-300 group-hover:text-white">
                    {c.title}
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-slate-500 transition-colors duration-300 group-hover:text-slate-300">
                    {c.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
            <Reveal>
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={IMAGES.twoPeopleTablet}
                  alt="Custom formulation review in the cleanroom"
                  className="h-full w-full object-cover"
                />
                <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-black/10" />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <Label>Custom Formulation vs. Private Label</Label>
              <h2 className="mt-6 font-heading text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
                Two paths, one standard of quality.
              </h2>
              <div className="mt-8 space-y-6">
                <div className="border-l-2 border-clinical pl-6">
                  <h3 className="font-heading text-xl font-bold text-navy">
                    Custom Formulation
                  </h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-slate-500">
                    We build formulations to your specification using
                    client-specified ingredients and target profiles.
                  </p>
                </div>
                <div className="border-l-2 border-slate-300 pl-6">
                  <h3 className="font-heading text-xl font-bold text-navy">
                    Private Label
                  </h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-slate-500">
                    We manufacture; you own the brand. Delivered finished and
                    labeled with a complete documentation package.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-navy py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="grid gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <Label light>Scalability</Label>
              <h2 className="mt-6 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                Batch sizes that grow with your brand.
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7 lg:self-center">
              <p className="text-lg leading-relaxed text-slate-300">
                Whether you're validating an initial run or scaling into sustained
                volume, our production adapts. We support brands from their first
                order through steady, repeatable manufacturing — with the same
                documentation and cleanroom standards at every scale.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
